// US-DEPOSIT-02 -- child submits a deposit that waits for guardian approval.

import {
  type AppError,
  type DepositSubmitPendingResult,
  bucketsList,
  depositSubmitPending,
} from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { AmountPad } from '../components/AmountPad';

type Props = {
  sessionId: string;
  currencyCode: string;
  onSubmitted: (result: DepositSubmitPendingResult) => void;
  onCancel: () => void;
};

const SOURCES = [
  { code: 'chore', label: 'Chore' },
  { code: 'allowance', label: 'Allowance' },
  { code: 'found', label: 'Found money' },
  { code: 'other', label: 'Other' },
];

export const SubmitPendingDepositScreen: Component<Props> = (props) => {
  const [bucketsResource] = createResource(async () => {
    const result = await bucketsList({ sessionId: props.sessionId });
    return result.ok ? result.value : [];
  });
  const [amountInput, setAmountInput] = createSignal('');
  const [sourceCode, setSourceCode] = createSignal('allowance');
  const [note, setNote] = createSignal('');
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const parseAmountMinorUnits = (): number | null => {
    const raw = amountInput().trim();
    if (raw === '' || !/^\d+(\.\d{1,2})?$/.test(raw)) return null;
    const major = Number.parseFloat(raw);
    return Number.isFinite(major) && major > 0 ? Math.round(major * 100) : null;
  };

  const splitPreview = createMemo(() => {
    const amountMinorUnits = parseAmountMinorUnits();
    const buckets = bucketsResource();
    if (amountMinorUnits === null || !buckets || buckets.length === 0) return [];

    const splittingBuckets = buckets
      .filter((bucket) => bucket.allocationBps > 0)
      .sort((left, right) => right.priority - left.priority || left.sortOrder - right.sortOrder);
    if (splittingBuckets.length === 0) return [];

    let remaining = amountMinorUnits;
    return splittingBuckets.map((bucket, index) => {
      const planned = Math.floor((amountMinorUnits * bucket.allocationBps) / 10_000);
      const amount = index === splittingBuckets.length - 1 ? remaining : planned;
      remaining -= amount;
      return { bucketName: bucket.bucketName, amountMinorUnits: amount };
    });
  });

  const formatAmount = (minorUnits: number) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: props.currencyCode,
      }).format(minorUnits / 100);
    } catch {
      return `${props.currencyCode} ${(minorUnits / 100).toFixed(2)}`;
    }
  };

  const handleSubmit = async () => {
    setErrorMessage(null);
    const minorUnits = parseAmountMinorUnits();
    if (minorUnits === null) {
      setErrorMessage('Enter an amount greater than zero (for example, 5 or 12.50).');
      return;
    }

    setSubmitting(true);
    try {
      const trimmedNote = note().trim();
      const result = await depositSubmitPending({
        sessionId: props.sessionId,
        amountMinorUnits: minorUnits,
        sourceCode: sourceCode(),
        ...(trimmedNote === '' ? {} : { note: trimmedNote }),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      setAmountInput('');
      setNote('');
      props.onSubmitted(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      eyebrow="Goes to Mum & Dad first"
      title="Add money"
      onBack={props.onCancel}
      class="action-screen--child-money"
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          disabled={submitting()}
          onClick={() => void handleSubmit()}
        >
          {submitting() ? 'Sending…' : 'Ask Mum & Dad'}
        </button>
      }
    >
      <AmountPad
        id="pending-deposit-amount"
        label="Amount"
        value={amountInput()}
        currencyCode={props.currencyCode}
        hint="They'll check you handed over the cash."
        disabled={submitting()}
        onChange={(value) => {
          setAmountInput(value);
          setErrorMessage(null);
        }}
      />

      <section class="action-choice-section" aria-labelledby="pending-deposit-source-label">
        <span id="pending-deposit-source-label" class="action-field-label">
          Where's it from?
        </span>
        <div class="action-chip-row">
          <For each={SOURCES}>
            {(source) => (
              <button
                type="button"
                class={`action-chip${sourceCode() === source.code ? ' is-selected' : ''}`}
                aria-pressed={sourceCode() === source.code}
                disabled={submitting()}
                onClick={() => setSourceCode(source.code)}
              >
                {source.label}
              </button>
            )}
          </For>
        </div>
      </section>

      <Show when={splitPreview().length > 0}>
        <section class="deposit-split-preview" aria-labelledby="deposit-split-preview-title">
          <div>
            <span class="action-field-label" id="deposit-split-preview-title">
              Once approved, it splits like this
            </span>
            <p>Your usual bucket split will be used.</p>
          </div>
          <ul>
            <For each={splitPreview()}>
              {(allocation) => (
                <li>
                  <span>{allocation.bucketName}</span>
                  <strong>{formatAmount(allocation.amountMinorUnits)}</strong>
                </li>
              )}
            </For>
          </ul>
        </section>
      </Show>

      <label class="action-note-field" for="pending-deposit-note">
        <span>
          Anything Mum or Dad should know? <em>Optional</em>
        </span>
        <input
          id="pending-deposit-note"
          type="text"
          autocomplete="off"
          disabled={submitting()}
          placeholder="For example, birthday money"
          value={note()}
          onInput={(event) => setNote(event.currentTarget.value)}
        />
      </label>

      <Show when={errorMessage()}>
        {(message) => (
          <div class="form-error" role="alert">
            {message()}
          </div>
        )}
      </Show>
    </ActionScreen>
  );
};

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
    case 'network_required':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    default:
      return `Something went wrong: ${error.message}`;
  }
}
